import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { X, Camera } from 'lucide-react';

interface QRScannerProps {
  onScanSuccess: (qrData: string) => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: string;
}

export const QRScanner: React.FC<QRScannerProps> = ({
  onScanSuccess,
  onClose,
  isLoading,
  error,
}) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const hasScannedRef = useRef(false);
  const onScanSuccessRef = useRef(onScanSuccess);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');

  // Keep the latest callback in a ref so the scanner effect can stay mount-once
  useEffect(() => {
    onScanSuccessRef.current = onScanSuccess;
  }, [onScanSuccess]);

  useEffect(() => {
    const initScanner = async () => {
      try {
        // Check camera permission
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
          setCameraPermission(result.state);
        }

        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'user' }, // Use front camera for wall-mounted devices
          {
            fps: 10, // Frames per second for scanning
            qrbox: { width: 250, height: 250 }, // Scanning box size
          },
          (decodedText) => {
            // Guard against multiple rapid fires before the scanner actually stops
            if (hasScannedRef.current) return;
            hasScannedRef.current = true;

            onScanSuccessRef.current(decodedText);
            scanner.stop().catch(() => {});
          },
          () => {
            // Per-frame decode errors — ignore ("no QR code found" noise)
          }
        );

        setIsScanning(true);
        setScanError(null);
      } catch (err: any) {
        console.error('Error initializing scanner:', err);
        setScanError(err.message || 'Nie udało się uruchomić kamery');
        setCameraPermission('denied');
      }
    };

    initScanner();

    return () => {
      const scanner = scannerRef.current;
      if (scanner && scanner.isScanning) {
        scanner.stop().catch((err) => console.error('Error stopping scanner:', err));
      }
    };
  }, []);

  const handleClose = () => {
    const scanner = scannerRef.current;
    if (scanner && scanner.isScanning) {
      scanner.stop().catch((err) => console.error('Error stopping scanner:', err));
    }
    onClose();
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center relative">
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-4 top-4"
          onClick={handleClose}
          disabled={isLoading}
        >
          <X className="w-5 h-5" />
        </Button>
        <div className="flex justify-center mb-4">
          <img
            src="/logo.png"
            alt="SZKŁO Sp. z o.o."
            className="h-16 w-auto"
          />
        </div>
        <CardTitle className="text-2xl font-bold">Skanuj kod QR</CardTitle>
        <CardDescription>
          Przyłóż kod QR do kamery, aby się zalogować
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Scanner Preview */}
        <div className="relative min-h-[300px]">
          <div
            id="qr-reader"
            className="w-full rounded-lg overflow-hidden border-2 border-primary min-h-[300px]"
          />

          {!isScanning && !scanError && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center rounded-lg z-10">
              <div className="text-center">
                <Camera className="w-12 h-12 mx-auto mb-2 text-gray-400 animate-pulse" />
                <p className="text-sm text-gray-500">Uruchamianie kamery...</p>
              </div>
            </div>
          )}
        </div>

        {/* Error Messages */}
        {(error || scanError) && (
          <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-md">
            {error || scanError}
          </div>
        )}

        {/* Camera Permission Denied */}
        {cameraPermission === 'denied' && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
            <p className="text-sm text-yellow-800 mb-2">
              <strong>Brak dostępu do kamery</strong>
            </p>
            <p className="text-xs text-yellow-700">
              Aby użyć skanera QR, zezwól na dostęp do kamery w ustawieniach przeglądarki.
            </p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">Logowanie...</p>
          </div>
        )}

        {/* Back to PIN Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={handleClose}
          disabled={isLoading}
        >
          Wróć do wprowadzania PIN
        </Button>
      </CardContent>
    </Card>
  );
};
